import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Layers, Store, BarChart3, Heart, Building2, Smartphone, Gamepad2, Brain,
  ArrowRight, Database, Shield, Server, Cloud, MonitorPlay, GitBranch,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

export interface UseCaseData {
  title: string;
  desc: string;
  icon: React.ElementType;
  architecture: { layer: string; components: string[]; icon: React.ElementType }[];
  dataModels: { name: string; fields: string[] }[];
  authSystem: string[];
  deployment: string[];
}

const useCaseDetails: Record<string, Omit<UseCaseData, "title" | "desc" | "icon">> = {
  saas: {
    architecture: [
      { layer: "Frontend", components: ["Dashboard UI", "Admin Panel", "User Portal", "Billing Page"], icon: MonitorPlay },
      { layer: "API Layer", components: ["REST API", "GraphQL Gateway", "Webhooks", "Rate Limiter"], icon: Server },
      { layer: "Database", components: ["PostgreSQL", "Redis Cache", "Search Index", "File Storage"], icon: Database },
      { layer: "Infrastructure", components: ["CDN", "Load Balancer", "Auto-scaling", "Monitoring"], icon: Cloud },
    ],
    dataModels: [
      { name: "Organization", fields: ["id", "name", "plan", "billing_status", "created_at"] },
      { name: "User", fields: ["id", "email", "role", "org_id", "last_login"] },
      { name: "Subscription", fields: ["id", "org_id", "plan_id", "status", "next_billing"] },
    ],
    authSystem: ["Email/Password", "OAuth (Google, GitHub)", "SSO/SAML", "Role-based access", "API keys", "2FA"],
    deployment: ["Multi-region hosting", "CI/CD pipeline", "Staging environment", "Blue-green deploy", "Auto-scaling"],
  },
  market: {
    architecture: [
      { layer: "Frontend", components: ["Buyer Portal", "Seller Dashboard", "Search UI", "Checkout"], icon: MonitorPlay },
      { layer: "API Layer", components: ["Product API", "Order API", "Payment Gateway", "Messaging"], icon: Server },
      { layer: "Database", components: ["Product Catalog", "Order Store", "User Profiles", "Reviews"], icon: Database },
      { layer: "Infrastructure", components: ["CDN", "Search Engine", "Payment Processing", "Notifications"], icon: Cloud },
    ],
    dataModels: [
      { name: "Product", fields: ["id", "seller_id", "title", "price", "category", "status"] },
      { name: "Order", fields: ["id", "buyer_id", "items", "total", "status", "tracking"] },
      { name: "Review", fields: ["id", "product_id", "user_id", "rating", "comment"] },
    ],
    authSystem: ["Buyer accounts", "Seller verification", "Admin roles", "OAuth login", "Session management"],
    deployment: ["Global CDN", "Payment PCI compliance", "Fraud detection", "Auto-scaling", "Backup strategy"],
  },
  fintech: {
    architecture: [
      { layer: "Frontend", components: ["Account Dashboard", "Transaction View", "Analytics", "Settings"], icon: MonitorPlay },
      { layer: "API Layer", components: ["Transaction API", "KYC Service", "Risk Engine", "Reporting"], icon: Server },
      { layer: "Database", components: ["Ledger DB", "User Store", "Audit Log", "Compliance Records"], icon: Database },
      { layer: "Infrastructure", components: ["Encryption Layer", "Compliance Monitor", "Failover", "Backup"], icon: Cloud },
    ],
    dataModels: [
      { name: "Account", fields: ["id", "user_id", "balance", "currency", "status", "tier"] },
      { name: "Transaction", fields: ["id", "from", "to", "amount", "type", "timestamp"] },
      { name: "AuditLog", fields: ["id", "action", "user_id", "details", "ip_address"] },
    ],
    authSystem: ["Multi-factor auth", "KYC verification", "Biometric login", "Session tokens", "IP whitelisting"],
    deployment: ["SOC 2 compliant hosting", "Encrypted storage", "Disaster recovery", "Real-time monitoring"],
  },
  health: {
    architecture: [
      { layer: "Frontend", components: ["Patient Portal", "Provider Dashboard", "Scheduling", "Records"], icon: MonitorPlay },
      { layer: "API Layer", components: ["Patient API", "Appointment API", "HL7/FHIR Gateway", "Messaging"], icon: Server },
      { layer: "Database", components: ["Patient Records", "Appointments", "Prescriptions", "Billing"], icon: Database },
      { layer: "Infrastructure", components: ["HIPAA Hosting", "Encrypted Storage", "Audit Logging", "Backup"], icon: Cloud },
    ],
    dataModels: [
      { name: "Patient", fields: ["id", "name", "dob", "insurance_id", "primary_provider"] },
      { name: "Appointment", fields: ["id", "patient_id", "provider_id", "datetime", "type", "status"] },
      { name: "MedicalRecord", fields: ["id", "patient_id", "diagnosis", "treatment", "date"] },
    ],
    authSystem: ["HIPAA-compliant auth", "Provider credentials", "Patient portal login", "Role-based access"],
    deployment: ["HIPAA hosting", "End-to-end encryption", "Audit compliance", "Failover systems"],
  },
  gov: {
    architecture: [
      { layer: "Frontend", components: ["Citizen Portal", "Service Forms", "Document Upload", "Status Tracker"], icon: MonitorPlay },
      { layer: "API Layer", components: ["Service API", "Document API", "Identity Verification", "Notifications"], icon: Server },
      { layer: "Database", components: ["Citizen Records", "Applications", "Documents", "Audit Trail"], icon: Database },
      { layer: "Infrastructure", components: ["Sovereign Hosting", "Accessibility Layer", "Security", "Archival"], icon: Cloud },
    ],
    dataModels: [
      { name: "Citizen", fields: ["id", "national_id", "name", "address", "verified_at"] },
      { name: "Application", fields: ["id", "citizen_id", "service_type", "status", "submitted_at"] },
    ],
    authSystem: ["National ID verification", "Multi-factor auth", "Accessibility compliance", "Audit logging"],
    deployment: ["Government cloud", "Data sovereignty", "High availability", "Disaster recovery"],
  },
  mobile: {
    architecture: [
      { layer: "Frontend", components: ["React Native UI", "Navigation", "Offline Storage", "Push Notifications"], icon: MonitorPlay },
      { layer: "API Layer", components: ["REST API", "WebSocket", "File Upload", "Auth Service"], icon: Server },
      { layer: "Database", components: ["User Store", "Content DB", "Media Storage", "Analytics"], icon: Database },
      { layer: "Infrastructure", components: ["CDN", "Push Service", "App Store Deploy", "Crash Reporting"], icon: Cloud },
    ],
    dataModels: [
      { name: "User", fields: ["id", "email", "device_token", "preferences", "last_active"] },
      { name: "Content", fields: ["id", "type", "data", "author_id", "created_at"] },
    ],
    authSystem: ["Biometric login", "OAuth providers", "Device tokens", "Session management"],
    deployment: ["App Store submission", "OTA updates", "CDN distribution", "Crash monitoring"],
  },
  game: {
    architecture: [
      { layer: "Scene", components: ["Scene Graph", "Camera Rig", "Lighting", "Environment"], icon: MonitorPlay },
      { layer: "Simulation", components: ["Rapier Physics", "Character Controller", "Collision", "NPC Behaviour"], icon: Server },
      { layer: "Gameplay", components: ["Inventory", "Quests", "Combat", "Progression"], icon: Database },
      { layer: "Runtime", components: ["Asset Loading", "Spatial Audio", "Animation", "Frustum Culling"], icon: Cloud },
    ],
    dataModels: [
      { name: "Player", fields: ["id", "position", "health", "inventory", "progress"] },
      { name: "SaveState", fields: ["id", "checkpoint", "unlocks", "stats", "updatedAt"] },
    ],
    authSystem: ["Local player profile", "Saved progress", "Per-device settings", "Accessibility options"],
    deployment: ["Static web build", "Installable PWA", "Browser runtime", "Local save persistence"],
  },
  ai: {
    architecture: [
      { layer: "Frontend", components: ["Dashboard UI", "Chart Components", "Data Explorer", "Reports"], icon: MonitorPlay },
      { layer: "API Layer", components: ["Data Pipeline", "ML Inference API", "Export Service", "Webhooks"], icon: Server },
      { layer: "Database", components: ["Time-series DB", "Model Store", "Feature Store", "Result Cache"], icon: Database },
      { layer: "Infrastructure", components: ["GPU Cluster", "Data Lake", "Stream Processing", "Monitoring"], icon: Cloud },
    ],
    dataModels: [
      { name: "Dataset", fields: ["id", "name", "source", "schema", "row_count", "updated_at"] },
      { name: "Model", fields: ["id", "name", "version", "accuracy", "status", "deployed_at"] },
    ],
    authSystem: ["Team accounts", "API keys", "Role-based dashboards", "SSO integration"],
    deployment: ["GPU-enabled hosting", "Data pipeline orchestration", "Auto-scaling inference", "Monitoring"],
  },
};

interface Props {
  /** `id` is the lookup key. The panel used to be keyed by the English display title, so
      renaming a card silently emptied its panel and every non-English visitor got nothing
      at all, since `title` arrives already translated. */
  useCase: { id: string; title: string; desc: string; icon: React.ElementType } | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function UseCasePreviewPanel({ useCase, open, onOpenChange }: Props) {
  const { t } = useI18n();
  if (!useCase) return null;
  const details = useCaseDetails[useCase.id];
  if (!details) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <useCase.icon className="h-5 w-5 text-primary" />
            </div>
            {useCase.title}
          </DialogTitle>
          <p className="text-muted-foreground text-sm">{useCase.desc}</p>
        </DialogHeader>

        <Tabs defaultValue="architecture" className="mt-4">
          <TabsList className="w-full grid grid-cols-4">
            <TabsTrigger value="architecture" className="text-xs">{t("usecase.tab.architecture")}</TabsTrigger>
            <TabsTrigger value="data" className="text-xs">{t("usecase.tab.data")}</TabsTrigger>
            <TabsTrigger value="auth" className="text-xs">{t("usecase.tab.auth")}</TabsTrigger>
            <TabsTrigger value="deploy" className="text-xs">{t("usecase.tab.deploy")}</TabsTrigger>
          </TabsList>

          <TabsContent value="architecture" className="space-y-3 mt-4">
            {details.architecture.map((layer, i) => (
              <motion.div
                key={layer.layer}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="rounded-xl border border-border/60 bg-muted/30 p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <layer.icon className="h-4 w-4 text-primary" />
                  <span className="font-semibold text-sm text-foreground">{layer.layer}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {layer.components.map((c) => (
                    <span key={c} className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium">{c}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </TabsContent>

          <TabsContent value="data" className="space-y-3 mt-4">
            {details.dataModels.map((model, i) => (
              <motion.div
                key={model.name}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="rounded-xl border border-border/60 bg-muted/30 p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Database className="h-4 w-4 text-primary" />
                  <span className="font-semibold text-sm text-foreground font-mono">{model.name}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {model.fields.map((f) => (
                    <span key={f} className="text-xs px-2 py-0.5 rounded bg-secondary text-secondary-foreground font-mono">{f}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </TabsContent>

          <TabsContent value="auth" className="mt-4">
            <div className="space-y-2">
              {details.authSystem.map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border/40 bg-muted/20"
                >
                  <Shield className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-sm text-foreground">{item}</span>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="deploy" className="mt-4">
            <div className="space-y-2">
              {details.deployment.map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border/40 bg-muted/20"
                >
                  <Cloud className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-sm text-foreground">{item}</span>
                </motion.div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-6 flex gap-3">
          <Button asChild className="flex-1">
            <Link to="/signup">
              {t("usecase.startBuilding")} <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("usecase.close")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
